import React from "react";
import { Flex, IconName, Notification, Text } from "@telia/teddy";
import { CreditCheckDecisionType } from "../../../../../../constants";
import "./Status.less";

interface StatusProps {
    creditCheckDecision?: CreditCheckDecisionType;
    creditCheckMessage?: string;
    hasCreditCheck: boolean;
    hasIdentification: boolean;
    hasLegalOwner: boolean;
    identificationMethod?: string;
    isExistingCustomer?: boolean;
    requiresCreditCheck: boolean;
}

const getIdentificationMethodText = (method?: string): string => {
    if (!method) return "";

    const methodMap: Record<string, string> = {
        DRIVERS_LICENSE: "Førerkort",
        PASSPORT: "Pass",
        NATIONAL_ID_CARD: "Nasjonalt ID-kort",
        BANK_ID: "BankID",
    };

    return methodMap[method] || method.toLowerCase();
};

const getDisplayableCreditCheckResult = (
    decision?: string,
    message?: string,
    isExistingCustomer?: boolean,
): {
    heading?: string;
    icon: IconName;
    message?: string;
    variant: "success" | "error" | "warning" | "information" | "beige";
} => {
    switch (decision) {
        case "OK":
            return { variant: "success", message: "Kredittsjekk godkjent", icon: "check-circle-filled" };
        case "CONTROL":
            return { heading: "Manuell kontroll påkrevd", variant: "information", message, icon: "info" };
        case "OK_WITH_CREDITLIMIT":
            return { heading: "Kredittsjekk godkjent med kredittgrense", variant: "warning", message, icon: "alert" };
        case "DENIED":
            return {
                heading: "Kredittsjekk avslått",
                variant: isExistingCustomer ? "information" : "error",
                message,
                icon: "error",
            };
        case "INVALID_NATIONAL_IDENTITY_NUMBER":
            return {
                heading: "Kredittsjekk avslått",
                variant: isExistingCustomer ? "warning" : "error",
                message,
                icon: "error",
            };
        default:
            return { variant: "beige", heading: "Klarte ikke å hente kredittsjekkstatus", icon: "question" };
    }
};

const Status: React.FC<StatusProps> = ({
    hasCreditCheck,
    creditCheckDecision = "",
    creditCheckMessage = "",
    hasIdentification,
    hasLegalOwner,
    identificationMethod,
    isExistingCustomer,
    requiresCreditCheck,
}) => {
    const renderCreditCheckStatus = () => {
        if (!requiresCreditCheck) {
            return (
                <Notification
                    type="tile"
                    variant={hasLegalOwner ? "success" : "information"}
                    className="status__notification"
                >
                    <Notification.Icon name={hasLegalOwner ? "check-circle-filled" : "info"} />
                    <Notification.Text>
                        {hasLegalOwner ? "Kundeoppslag gjennomført" : "Kundeoppslag ikke gjennomført"}
                    </Notification.Text>
                </Notification>
            );
        }

        if (!hasCreditCheck) {
            return (
                <Notification type="tile" variant="warning" className="status__notification">
                    <Notification.Icon />
                    <Notification.Text>Kredittsjekk ikke gjennomført</Notification.Text>
                </Notification>
            );
        }

        const result = getDisplayableCreditCheckResult(creditCheckDecision, creditCheckMessage, isExistingCustomer);
        return (
            <Notification type="tile" variant={result.variant} className="status__notification">
                <Notification.Icon name={result.icon} />
                <Notification.Text>
                    <div>
                        {result.heading && (
                            <Text>
                                <strong>{result.heading}</strong>
                            </Text>
                        )}
                        {result.message &&
                            (creditCheckDecision === "DENIED" || creditCheckDecision === "SCORE_NOT_AVAILABLE" ? (
                                <Text>
                                    <span dangerouslySetInnerHTML={{ __html: result.message }} />
                                </Text>
                            ) : (
                                <Text>{result.message}</Text>
                            ))}
                    </div>
                </Notification.Text>
            </Notification>
        );
    };

    const renderIdentificationStatus = () => {
        if (hasIdentification) {
            return (
                <Notification type="tile" variant="success" className="status__notification">
                    <Notification.Icon name="check-circle-filled" />
                    <Notification.Text>
                        Identifisering gjennomført med{" "}
                        {identificationMethod ? getIdentificationMethodText(identificationMethod).toLowerCase() : ""}
                    </Notification.Text>
                </Notification>
            );
        }

        return (
            <Notification type="tile" variant="warning" className="status__notification">
                <Notification.Icon />
                <Notification.Text>Identifisering ikke gjennomført</Notification.Text>
            </Notification>
        );
    };

    return (
        <Flex direction="column">
            {renderCreditCheckStatus()}
            {renderIdentificationStatus()}
        </Flex>
    );
};

export default Status;
